import SVG from 'react-inlinesvg';
import classNames from './GenerateReport.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useState, useRef, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import StudentReport from './StudentReport';
import AppModal from '@/components/AppModal/AppModal';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import ReportErrorModal from './ReportErrorModal';
import downLoadSvg from '@/assets/svg/download.svg';

export type GenerateReportProps = {
  schoolId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  children?: any;
};

const GenerateReport = ({
  schoolId,
  studentId,
  firstName,
  lastName,
  children,
}: GenerateReportProps) => {
  const { t } = useAdminTranslation();

  const [loadingReport, setLoadingReport] = useState(false);
  const [isErrorDownloading, setIsErrorDownloading] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const isGeneratingRef = useRef(false);
  const reportRef = useRef<HTMLElement | null>(null);
  const [reportReady, setReportReady] = useState(false);

  const generateReportPdf = async () => {
    const element = reportRef.current;
    if (!element) return;
    if (isGeneratingRef.current) return;

    isGeneratingRef.current = true;
    element.style.display = 'flex';
    element.style.visibility = 'visible';
    element.dataset.reportrendering = '1';

    // Temporarily disable zoom on parent containers during PDF generation
    const originalZooms: { element: Element; zoom: string }[] = [];
    let currentElement: Element | null = element.parentElement;

    while (currentElement) {
      const computedStyle = window.getComputedStyle(currentElement);
      const zoom = (computedStyle as any).zoom;
      if (zoom !== '1') {
        originalZooms.push({ element: currentElement, zoom });
        (currentElement as HTMLElement).style.setProperty('zoom', '1');
      }
      currentElement = currentElement.parentElement;
    }

    // Wait 2 seconds (setTimeout) to let the report DOM/styles/assets fully render before capture
    await new Promise<void>(resolve => setTimeout(resolve, 2000));

    try {
      const canvas = await html2canvas(element, { scale: 5 });

      element.style.display = 'none';
      element.dataset.reportrendering = '0';

      // Restore original zoom values
      originalZooms.forEach(({ element, zoom }) => {
        (element as HTMLElement).style.setProperty('zoom', zoom);
      });

      const imgData = canvas.toDataURL('image/jpeg');
      const pdf = new jsPDF('p', 'mm', [297, 210]);

      pdf.setFillColor(251, 251, 251);
      pdf.rect(0, 0, 210, 297, 'F');

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const hRatio = imgHeight / imgWidth;

      const width = pdf.internal.pageSize.getWidth();
      const height = width * hRatio;

      pdf.addImage(imgData, 'JPEG', width * 0, 0, width * 1, height * 0.95);

      const pdfOutput = pdf.output('blob');
      const filename = `${t('studentDataModal.report.studentReport')}.pdf`;

      const url = window.URL.createObjectURL(pdfOutput);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);

      a.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);

      setLoadingReport(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Restore original zoom values even if there's an error
      originalZooms.forEach(({ element, zoom }) => {
        (element as HTMLElement).style.setProperty('zoom', zoom);
      });
      setIsErrorDownloading(true);
      setLoadingReport(false);
    } finally {
      isGeneratingRef.current = false;
      setReportReady(false);
    }
  };

  const onLoadReportError = useCallback(() => {
    setIsServerError(true);
    setLoadingReport(false);
  }, []);

  const closeErrorModal = () => {
    setIsServerError(false);
    setIsErrorDownloading(false);
    setLoadingReport(false);
  };

  useEffect(() => {
    if (loadingReport && reportReady) {
      generateReportPdf();
    }
  }, [loadingReport, reportReady]);

  useEffect(() => {
    return () => {
      isGeneratingRef.current = false;
    };
  }, []);

  const setReportRef = (el: HTMLElement | null) => {
    reportRef.current = el;
    if (el) setReportReady(true);
  };

  return (
    <>
      {loadingReport && (
        <AppModal
          modalContentClassName={classNames.appModal}
          show={true}
          showBackdrop={false}
          blockBodyScroll={true}
        >
          <AppLoaderCircle />
        </AppModal>
      )}

      <ReportErrorModal
        show={isErrorDownloading || isServerError}
        onClose={closeErrorModal}
        isStudentReport={true}
      />

      <button
        className={classNames.button}
        onClick={() => {
          setLoadingReport(true);
        }}
      >
        {children || (
          <>
            <SVG src={downLoadSvg} />
            <h2>{t('studentDataModal.report.generateReport')}</h2>
          </>
        )}
      </button>

      {loadingReport && schoolId && studentId && (
        <StudentReport
          schoolId={schoolId}
          studentId={studentId}
          firstName={firstName}
          lastName={lastName}
          generateReportPdf={setReportRef}
          onLoadReportError={onLoadReportError}
        />
      )}
    </>
  );
};

export default GenerateReport;
